import {ref} from 'vue';
import type {ICallerIdEntry, ICallerIdIncomingCallEvent} from '@torim-app/types';

import {CustomersApi} from '@app/api/admin/customers-api';

/**
 * Desktop side of caller ID: when the owner's Android phone recognises a customer calling,
 * the backend pushes the card over /ws/caller-id and we show it in the corner
 * (components/admin/components/IncomingCallCard.vue). Web only — on the phone itself
 * the native overlay already shows it.
 */

const AUTO_DISMISS_MS = 60_000;
const MAX_BACKOFF_MS = 60_000;

// Module-level: one socket per tab, shared by whoever renders the card.
export const incomingCall = ref<ICallerIdEntry | null>(null);

let socket: WebSocket | null = null;
let stopped = true;
let backoffMs = 2_000;
let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
let dismissTimer: ReturnType<typeof setTimeout> | undefined;

const buildWsUrl = (token: string): string => {
  const wsBase = `${import.meta.env.VITE_BACKEND_URL}`
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://');
  return `${wsBase}/ws/caller-id?token=${encodeURIComponent(token)}`;
};

export const dismissIncomingCall = () => {
  clearTimeout(dismissTimer);
  incomingCall.value = null;
};

const showIncomingCall = (entry: ICallerIdEntry) => {
  incomingCall.value = entry;
  clearTimeout(dismissTimer);
  dismissTimer = setTimeout(dismissIncomingCall, AUTO_DISMISS_MS);

  // Tab in the background: also raise a system notification (only if already allowed —
  // we never prompt for permission from here).
  if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
    const body = entry.nextAppointment ? `התור הבא: ${entry.nextAppointment.display}` : 'אין תור עתידי';
    const notification = new Notification(`📞 ${entry.fullName} מתקשר/ת`, {body, tag: 'timeit-caller-id'});
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  }
};

const scheduleReconnect = () => {
  if (stopped) return;
  clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(connect, backoffMs);
  backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
};

async function connect() {
  if (stopped || socket) return;
  try {
    const {token} = await CustomersApi.fetchCallerIdLiveToken();
    if (stopped) return;
    const ws = new WebSocket(buildWsUrl(token));
    socket = ws;
    ws.onopen = () => {
      backoffMs = 2_000;
    };
    ws.onmessage = (message) => {
      try {
        const event = JSON.parse(message.data) as ICallerIdIncomingCallEvent;
        if (event?.type === 'incoming-call' && event.entry) showIncomingCall(event.entry);
      } catch {
        // ignore malformed frames
      }
    };
    ws.onclose = () => {
      if (socket === ws) socket = null;
      scheduleReconnect();
    };
  } catch (e) {
    // Only started for users allowed to see contact details, so failures here are
    // network/server hiccups: retry with backoff (capped at 1 min).
    console.warn('[caller-id] live connection failed', e);
    scheduleReconnect();
  }
}

export const startIncomingCallLive = () => {
  if (!stopped) return;
  stopped = false;
  backoffMs = 2_000;
  connect();
};

export const stopIncomingCallLive = () => {
  stopped = true;
  clearTimeout(reconnectTimer);
  socket?.close();
  socket = null;
  dismissIncomingCall();
};
